import React, { useState } from "react";
import api from "../api";
import {
  HiOutlineLockClosed,
  HiOutlineKey,
  HiOutlineShieldCheck,
  HiOutlineXMark,
  HiOutlineEye,
  HiOutlineEyeSlash
} from "react-icons/hi2";

function ResetPassword({ user, onClose, homePageLabel = "Dashboard" }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const updatePassword = async () => {
    setMessage("");

    const currentValue = currentPassword.trim();
    const newValue = newPassword.trim();
    const confirmValue = confirmPassword.trim();

    if (!currentValue || !newValue || !confirmValue) {
      setMessage("⚠ Please fill all fields");
      return;
    }

    if (newValue.length < 6) {
      setMessage("❌ New password must be at least 6 characters");
      return;
    }

    if (newValue === currentValue) {
      setMessage("❌ New password must be different from current password");
      return;
    }

    if (newValue !== confirmValue) {
      setMessage("❌ Passwords do not match");
      return;
    }

    try {
      setLoading(true);

      await api.post("/reset-password", {
        user_id: user.id,
        current_password: currentValue,
        password: newValue
      });

      setMessage("✅ Password updated successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");

      setTimeout(() => {
        onClose();
      }, 900);
    } catch (err) {
      setMessage(err.response?.data?.error || "❌ Update failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={overlay}>
      <div style={modal}>
        <button style={closeBtn} onClick={onClose}>
          <HiOutlineXMark />
        </button>

        <div style={header}>
          <div style={badge}>Security</div>
          <h2 style={title}>Reset Password</h2>
          <p style={subtitle}>
            Update your password securely and return to {homePageLabel}.
          </p>
        </div>

        <div style={form}>
          <div style={field}>
            <label style={label}>Current Password</label>
            <div style={inputWrap}>
              <HiOutlineLockClosed style={inputIcon} />
              <input
                type={showCurrent ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => {
                  setCurrentPassword(e.target.value);
                  setMessage("");
                }}
                style={input}
                placeholder="Enter current password"
              />
              <button
                type="button"
                style={eyeBtn}
                onClick={() => setShowCurrent((prev) => !prev)}
              >
                {showCurrent ? <HiOutlineEyeSlash /> : <HiOutlineEye />}
              </button>
            </div>
          </div>

          <div style={field}>
            <label style={label}>New Password</label>
            <div style={inputWrap}>
              <HiOutlineKey style={inputIcon} />
              <input
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setMessage("");
                }}
                style={input}
                placeholder="Enter new password"
              />
              <button
                type="button"
                style={eyeBtn}
                onClick={() => setShowNew((prev) => !prev)}
              >
                {showNew ? <HiOutlineEyeSlash /> : <HiOutlineEye />}
              </button>
            </div>
          </div>

          <div style={field}>
            <label style={label}>Confirm Password</label>
            <div style={inputWrap}>
              <HiOutlineShieldCheck style={inputIcon} />
              <input
                type={showConfirm ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setMessage("");
                }}
                style={input}
                placeholder="Re-enter new password"
              />
              <button
                type="button"
                style={eyeBtn}
                onClick={() => setShowConfirm((prev) => !prev)}
              >
                {showConfirm ? <HiOutlineEyeSlash /> : <HiOutlineEye />}
              </button>
            </div>
          </div>

          <div style={buttonRow}>
            <button
              type="button"
              style={{
                ...primaryBtn,
                opacity: loading ? 0.75 : 1,
                cursor: loading ? "not-allowed" : "pointer"
              }}
              onClick={updatePassword}
              disabled={loading}
            >
              {loading ? "Updating..." : "Update Password"}
            </button>

            <button
              type="button"
              style={secondaryBtn}
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
          </div>

          {message && (
            <div
              style={{
                ...messageBox,
                background:
                  message.includes("✅") ? "#ECFDF5" : "#FEF2F2",
                color:
                  message.includes("✅") ? "#16A34A" : "#DC2626",
                border:
                  message.includes("✅")
                    ? "1px solid #BBF7D0"
                    : "1px solid #FECACA"
              }}
            >
              {message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const theme = {
  primary: "#0F766E",
  primaryDark: "#115E59",
  primaryLight: "#14B8A6",
  accent: "#2DD4BF",
  background: "#F8FAFC",
  surface: "#FFFFFF",
  surfaceSoft: "#ECFEFF",
  text: "#0F172A",
  textMuted: "#64748B",
  border: "#E2E8F0"
};

const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.35)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "24px",
  zIndex: 1200
};

const modal = {
  width: "100%",
  maxWidth: "560px",
  background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
  borderRadius: "24px",
  padding: "28px",
  position: "relative",
  boxShadow: "0 24px 60px rgba(15,23,42,0.18)",
  border: "1px solid #E6FFFB",
  fontFamily: "sans-serif"
};

const closeBtn = {
  position: "absolute",
  top: "18px",
  right: "18px",
  width: "38px",
  height: "38px",
  borderRadius: "12px",
  border: "none",
  background: theme.surfaceSoft,
  color: theme.primaryDark,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px"
};

const header = {
  marginBottom: "22px"
};

const badge = {
  display: "inline-block",
  padding: "6px 12px",
  borderRadius: "999px",
  background: theme.surfaceSoft,
  color: theme.primaryDark,
  fontSize: "12px",
  fontWeight: 700,
  marginBottom: "12px"
};

const title = {
  margin: 0,
  fontSize: "28px",
  fontWeight: 800,
  color: theme.text
};

const subtitle = {
  margin: "8px 0 0 0",
  color: theme.textMuted,
  fontSize: "14px",
  lineHeight: 1.6
};

const form = {
  display: "flex",
  flexDirection: "column",
  gap: "16px"
};

const field = {
  display: "flex",
  flexDirection: "column",
  gap: "8px"
};

const label = {
  fontSize: "14px",
  fontWeight: 700,
  color: theme.text
};

const inputWrap = {
  position: "relative"
};

const inputIcon = {
  position: "absolute",
  left: "14px",
  top: "50%",
  transform: "translateY(-50%)",
  fontSize: "18px",
  color: theme.textMuted
};

const input = {
  width: "100%",
  height: "48px",
  borderRadius: "14px",
  border: `1px solid ${theme.border}`,
  padding: "0 44px 0 44px",
  fontSize: "14px",
  outline: "none",
  boxSizing: "border-box",
  background: "#FFFFFF",
  color: theme.text
};

const eyeBtn = {
  position: "absolute",
  right: "12px",
  top: "50%",
  transform: "translateY(-50%)",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: theme.textMuted,
  fontSize: "18px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
};

const buttonRow = {
  display: "flex",
  gap: "12px",
  marginTop: "8px"
};

const primaryBtn = {
  flex: 1,
  height: "48px",
  borderRadius: "14px",
  border: "none",
  background: "linear-gradient(135deg, #0F766E 0%, #115E59 100%)",
  color: "#FFFFFF",
  fontSize: "14px",
  fontWeight: 700
};

const secondaryBtn = {
  flex: 1,
  height: "48px",
  borderRadius: "14px",
  border: "1px solid #CBD5E1",
  background: "#FFFFFF",
  color: "#0F172A",
  fontSize: "14px",
  fontWeight: 700,
  cursor: "pointer"
};

const messageBox = {
  marginTop: "6px",
  padding: "12px 14px",
  borderRadius: "14px",
  fontSize: "13px",
  fontWeight: 700
};

export default ResetPassword;